'use strict';

const TWENTY_YEARS_IN_SEC = 630720000;
const TUTORIAL_SHOWN_COOKIE_NAME = 'clo-cash-back-tutorial-shown';
const TUTORIAL_MODAL_SHOWN_COOKIE_NAME = 'clo-cash-back-tutorial-modal-shown';

const { windowLoaded } = require('@grpn/document-states');

const {
  displayCardsUserNeedsToClaimDeal,
  getUserBillingRecordTypes,
} = require('../../frontend/helper');

function getIntersection(a, b) {
  const setA = new Set(a);
  const setB = new Set(b);
  const intersection = new Set(Array.from(setA).filter(x => setB.has(x)));

  return Array.from(intersection);
}

function setIframeHeight(iframeElement) {
  const iframeContentHeight =
    iframeElement.contentWindow.document.body.scrollHeight;

  const { paddingTop, paddingBottom } = getComputedStyle(iframeElement);
  iframeElement.height = `${
    iframeContentHeight + parseInt(paddingTop) + parseInt(paddingBottom)
  }px`;
}

const tooltipConfig = {
  id: 'cash-back-tutorial-tooltip',
  config: {
    interactive: true,
    maxWidth: 787,
    placement: 'bottom',
    trigger: 'click',
  },
};

function getTutorialTooltipConfig(cookieDomain) {
  if (window.Cookie.get(TUTORIAL_SHOWN_COOKIE_NAME)) {
    return {};
  }
  if (!cookieDomain) {
    cookieDomain = window.location.hostname;
  }

  return {
    onShown() {
      window.Cookie.set(
        TUTORIAL_SHOWN_COOKIE_NAME,
        'true',
        TWENTY_YEARS_IN_SEC,
        cookieDomain
      );
    },
    showOnInit: true,
  };
}

function setCashbackTooltip(delay = 0) {
  const trigger = document.querySelector('.cash-back .tooltip-target');

  if (!trigger) {
    return;
  }

  const { parentNode } = trigger;

  setTimeout(() => {
    trigger.classList.add('tooltip-trigger');
    trigger.dataset['tooltipId'] = tooltipConfig.id;
    trigger.dataset['tippyConfig'] = JSON.stringify(tooltipConfig.config);
    if (tooltipConfig.config.showOnInit) {
      window.GIG.Tooltip.init(parentNode, tooltipConfig.config);
    } else {
      window.GIG.Tooltip.init(parentNode);
    }
  }, delay);
}

const CashBack = {
  init(payload, done) {
    let tooltipDelay;
    const { cashBack, cookieDomain } = payload;
    getUserBillingRecordTypes(this.userTypeHandler, payload);

    if (cashBack && cashBack.showTutorialTooltipOnLoad) {
      //If there's a banner coming from the layout service, due to its lazy loaded image we don't know when the page will be ready
      //so wait a little bit launching the tutorial popup. If there is no banner, launch right away
      tooltipDelay = document.querySelector('#ls-in-app-messages:not(.hide)')
        ? (cashBack ? cashBack.tooltipDelay : undefined) || 2000
        : 0;
      Object.assign(
        tooltipConfig.config,
        getTutorialTooltipConfig(cookieDomain)
      );
    }

    if (cashBack && cashBack.showSideTutorial) {
      const cloIframe = document.getElementById('clo-tutorial-iframe');
      if (cloIframe) {
        cloIframe.onload = () => setIframeHeight(cloIframe);
      }
    }

    windowLoaded.then(() => {
      if (document.getElementById('cash-back-tutorial-tooltip')) {
        const tooltipiframe = document.getElementById(
          'clo-tutorial-tooltip-iframe'
        );
        if (tooltipiframe) {
          tooltipiframe.onload = () => {
            const cashBackTutorial = document.querySelector(
              '.tippy-content #cash-back-tutorial-tooltip'
            );
            if (
              !cashBackTutorial ||
              !cashBackTutorial.classList.contains('has-loaded')
            ) {
              setIframeHeight(tooltipiframe);
              if (cashBackTutorial) {
                cashBackTutorial.classList.add('has-loaded');
              }
            }
          };

          if (
            !document.querySelector(
              '.tippy-content #cash-back-tutorial-tooltip'
            )
          ) {
            tooltipiframe.src = tooltipiframe.dataset.lazyload;
          }
        }

        if (
          !document.querySelector('.tippy-content #cash-back-tutorial-tooltip')
        ) {
          setCashbackTooltip(tooltipDelay);
        }
      }
    });

    done();
  },

  userTypeHandler(userTypes, data) {
    const { cashBack, cookieDomain } = data;
    // temp until preact buy is rolled out
    displayCardsUserNeedsToClaimDeal(
      userTypes,
      cashBack,
      '.buy-button:not(.pay-to-claim) #buy'
    );

    const onModalClose = () =>
      window.Cookie.set(
        TUTORIAL_MODAL_SHOWN_COOKIE_NAME,
        'true',
        TWENTY_YEARS_IN_SEC,
        cookieDomain
      );

    function showTutorialModal() {
      if (window.Cookie.get(TUTORIAL_MODAL_SHOWN_COOKIE_NAME)) {
        return;
      }
      window.GIG.Modal.open({ id: 'clo-tutorial-modal' });
      window.GIG.Modal.onClose(onModalClose, 'clo-tutorial-modal', true);

      const $tutorialIframe = document.querySelector(
        '#clo-modal-tutorial-iframe'
      );
      if ($tutorialIframe) {
        $tutorialIframe.onload = () => setIframeHeight($tutorialIframe);
      }
    }

    function showEnrollmentModal() {
      if (window.Cookie.get(TUTORIAL_MODAL_SHOWN_COOKIE_NAME)) {
        return;
      }
      window.GIG.Modal.open({ id: 'clo-enrollment-modal' });
      window.GIG.Modal.onClose(onModalClose, 'clo-enrollment-modal', true);

      const $enrollmentIframe = document.querySelector(
        '#clo-modal-enrollment-iframe'
      );
      if ($enrollmentIframe) {
        $enrollmentIframe.onload = () => setIframeHeight($enrollmentIframe);

        window.addEventListener(
          'message',
          e => {
            if (e.data === 'cards_consented') {
              window.GIG.Modal.close();
              const $enrollSuccess = document.querySelector(
                '#card-enroll-success'
              );
              if ($enrollSuccess) {
                $enrollSuccess.style.display = 'block';
              }
            }

            if (e.data === 'card_enrollment_error') {
              setIframeHeight($enrollmentIframe);
            }
          },
          false
        );
      }
    }

    const acceptableBillingRecordTypes = cashBack.acceptableBillingRecordTypes.map(
      type => type.cardType
    );
    const userHasProperCardTypeUnenrolled = getIntersection(
      userTypes.userUnconsentedTypes,
      acceptableBillingRecordTypes
    ).length;

    if (cashBack.showCLOModalOnLoad) {
      if (
        userTypes.userConsentedTypes &&
        userTypes.userConsentedTypes.length > 0
      ) {
        return;
      } else if (userHasProperCardTypeUnenrolled) {
        showEnrollmentModal(cookieDomain);
      } else {
        showTutorialModal(cookieDomain);
      }
    }
  },
};

module.exports = CashBack;
